import { useNavigate, useOutletContext, useParams } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import {
  ApprovalGroupDetailsFragment,
  ApprovalGroupRootPageDocument,
  GetRulesConfigurationDocument,
  UpdateRulesConfigurationDocument,
} from 'generated/resources/graphql'

import Loading from 'modules/core/components/Loading'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'

import {
  Form,
  rulesConfigurationsFormValues,
} from 'modules/approvals/components/RulesConfiguration/Form'
import { approvalFlowToArgs } from 'modules/approvals/lib/approvalFlows'

const UpdateApprovalRulePage = () => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const params = useParams()
  const { rules_configuration_id } = params
  const { group } = useOutletContext<{ group: ApprovalGroupDetailsFragment }>()

  const {
    company: { id: companyId },
  } = useCurrentSession()

  const { data, loading } = useQuery(GetRulesConfigurationDocument, {
    variables: {
      companyId,
      category: group.category,
      id: parseInt(String(rules_configuration_id)),
    },
  })

  const [updateRulesConfiguration] = useMutation(UpdateRulesConfigurationDocument, {
    extractorFn: (updateData) => updateData.approvalsMutations.updateApprovalsRulesConfiguration,
  })

  if (loading) {
    return <Loading />
  }

  const rulesConfiguration = data?.approvals.rulesConfiguration

  if (!group) {
    return null
  }

  if (!rulesConfiguration) {
    return null
  }

  const onSuccess = (_data: rulesConfigurationsFormValues) => {
    navigate(
      navigation.approvals.approvalGroupsCategoryId.details.fullPath({
        id: group.id,
        category: group.category,
      })
    )
  }

  const onSubmit = async (data: rulesConfigurationsFormValues) => {
    return updateRulesConfiguration({
      variables: {
        id: rulesConfiguration.id,
        name: data.name,
        isTemplate: false,
        flow: data.flow,
        onRulesMet: data.onRulesMet,
        rules:
          data.rules?.map(({ identifier, inputType, label, operation, value }) => ({
            identifier,
            inputType,
            label,
            operation,
            value,
          })) || [],
      },
      update(cache) {
        cache.evict({ id: cache.identify(rulesConfiguration) })
      },
      awaitRefetchQueries: true,
      refetchQueries: [
        {
          query: ApprovalGroupRootPageDocument,
          variables: {
            id: group.id,
            companyId,
            category: group.category,
          },
        },
      ],
    })
  }

  const rules = rulesConfiguration.rules

  const domainConfigurationRules = group?.domainConfigurationRules

  const variables = {
    name: rulesConfiguration.name || '',
    isTemplate: false,
    onRulesMet: rulesConfiguration.onRulesMet,
    flow: rulesConfiguration.flow ? approvalFlowToArgs(rulesConfiguration.flow) : null,
    rules: rules,
  }

  return (
    <Box flexGrow paddingX="s0" paddingY="s0" width="full">
      <Form
        action={'update'}
        variables={variables}
        domainConfigurationRules={domainConfigurationRules}
        onSubmit={(flowData) => onSubmit(flowData)}
        onSuccess={(flowData) => onSuccess(flowData)}
      />
    </Box>
  )
}

export default UpdateApprovalRulePage
